import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import Terminal from "@/components/Terminal";
import { Loader2, Cpu, Wrench } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["shellcode", "stager", "macro", "webshell", "beacon", "RCE chain"];
const ARCHS = ["x86_64", "Win32", "Win64", ".NET", "JVM", "PHP"];
const ENCODERS = ["shikata-ga-nai", "base64+gzip", "vba+obfuscated", "rot13+eval", "custom-XOR", "raw"];

const ExploitLab = () => {
  const [data, setData] = useState(null);
  const [build, setBuild] = useState({ type: TYPES[0], arch: ARCHS[0], encoding: ENCODERS[0] });
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [artifact, setArtifact] = useState(null);

  useEffect(() => { api.get("/exploit-lab/payloads").then(({ data }) => setData(data)); }, []);

  const runBuild = async () => {
    setBusy(true);
    setLog([{ type: "cmd", text: `build --type ${build.type} --arch ${build.arch} --enc ${build.encoding}` }]);
    setArtifact(null);
    try {
      const { data: r } = await api.post("/exploit-lab/build", build);
      for (let i = 0; i < r.build_log.length; i++) {
        await new Promise((res) => setTimeout(res, 300));
        setLog((prev) => [...prev, { type: "out", text: r.build_log[i] }]);
      }
      setArtifact(r);
      toast.success(`Build ${r.id} ready (AV-evasion ${r.av_evasion}%)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="p-6 text-cyan-400"><Loader2 className="animate-spin h-5 w-5" /></div>;

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Attack Surface / Lab"
        title="Exploit Lab"
        accent="pink"
        subtitle="Build, encode, and detonate payloads in an isolated sandbox before promoting them to the Pen Test Suite."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {[
          ["Library Modules", data.library_total, "cyan"],
          ["Custom Payloads", data.payloads.length, "fuchsia"],
          ["Sandbox Cores", data.sandbox_cores, "emerald"],
        ].map(([k, v, c]) => (
          <div key={k} className="csx-card p-4">
            <div className={`font-display text-3xl font-black text-${c}-400`}>{v}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{k}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3 flex items-center gap-2">
            <Wrench className="h-3 w-3" /> Payload Builder
          </div>

          {[
            ["Type", "type", TYPES],
            ["Target Arch", "arch", ARCHS],
            ["Encoder", "encoding", ENCODERS],
          ].map(([label, key, opts]) => (
            <div key={key} className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
              <div className="grid grid-cols-3 gap-1.5">
                {opts.map((o) => (
                  <button
                    key={o}
                    data-testid={`build-${key}-${o}`}
                    onClick={() => setBuild({ ...build, [key]: o })}
                    className={[
                      "text-[10px] uppercase tracking-widest p-1.5 border transition-all truncate",
                      build[key] === o ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300" : "border-slate-800 text-slate-400 hover:border-slate-700",
                    ].join(" ")}
                  >{o}</button>
                ))}
              </div>
            </div>
          ))}

          <TButton data-testid="build-run" variant="accent" icon={busy ? Loader2 : Cpu} className="w-full mt-2" onClick={runBuild} disabled={busy}>
            {busy ? "Compiling..." : "Compile & Sandbox-Test"}
          </TButton>

          {artifact && (
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">ARTIFACT</span><span className="font-mono text-cyan-300">{artifact.id}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">SIZE</span><span className="font-mono">{artifact.size_bytes} bytes</span></div>
              <div className="flex justify-between"><span className="text-slate-400">AV EVASION</span><span className={`font-mono ${artifact.av_evasion >= 85 ? "text-emerald-300" : "text-amber-300"}`}>{artifact.av_evasion}%</span></div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <Terminal lines={log} height="h-56" prompt="csx@lab ~$" />

          <div className="csx-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800 text-[10px] uppercase tracking-[0.3em] text-cyan-400">
              Recent Builds
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="py-2 px-3">Name</th>
                  <th className="px-3">Type</th>
                  <th className="px-3">Arch</th>
                  <th className="px-3">Size</th>
                  <th className="px-3">AV</th>
                </tr>
              </thead>
              <tbody>
                {data.payloads.map((p) => (
                  <tr key={p.id} className="border-b border-slate-900 hover:bg-slate-900/40">
                    <td className="py-2 px-3 text-slate-100">{p.name}</td>
                    <td className="px-3 text-slate-400">{p.type}</td>
                    <td className="px-3 font-mono text-cyan-300">{p.target_arch}</td>
                    <td className="px-3 font-mono text-slate-400">{p.size_bytes}</td>
                    <td className={`px-3 font-mono ${p.av_evasion >= 85 ? "text-emerald-300" : "text-amber-300"}`}>{p.av_evasion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploitLab;
